// src/features/calendar/routes/CalendarPage.tsx
import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useCalendarStore } from '../store/calendarStore';
import { Bars3Icon } from '@heroicons/react/24/outline';

import FullCalendarView from '../components/FullCalendarView';
import CalendarSidebar from '../components/CalendarSidebar';
import EventForm from '../components/EventForm';
import EventDetailModal from '../components/EventDetailModal';
// import EventTypeFilters from '../components/EventTypeFilters'; // Moved to CalendarSidebar
import { CalendarDataProvider } from '../components/CalendarDataProvider';
import RealTimeNotifications from '../components/RealTimeNotifications';

import type { CalendarEvent } from '../types/calendar.types';
import { useUserPreferencesStore } from '@/store/userPreferencesStore';

const CalendarPage: React.FC = () => {
  console.log('🔥🔥🔥 [CalendarPage] CALENDAR PAGE IS LOADING - THIS SHOULD ALWAYS SHOW! 🔥🔥🔥');
  
  const {
    currentView,
    setCurrentView,
    setCurrentDate,
    events,
    isEventFormOpen,
    setEventFormOpen,
    isCalendarSidebarOpen,
    setCalendarSidebarOpen,
  } = useCalendarStore();
  const { appPreferences, hasHydrated } = useUserPreferencesStore();
  const lastAppliedView = useRef(appPreferences.calendar.defaultView);
  const [searchParams] = useSearchParams();
  const handledDeepLinkRef = useRef<string | null>(null);

  // Local state for event detail modal and editing
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);
  const [slotSelection, setSlotSelection] = useState<{ start: Date; end: Date } | null>(null);

  // Data initialization is now handled by CalendarDataProvider
  useEffect(() => {
    if (!hasHydrated) return;
    const preferredView = appPreferences.calendar.defaultView;
    if (currentView === lastAppliedView.current && preferredView !== currentView) {
      setCurrentView(preferredView);
    }
    lastAppliedView.current = preferredView;
  }, [appPreferences.calendar.defaultView, currentView, hasHydrated, setCurrentView]);

  useEffect(() => {
    const eventId = searchParams.get('eventId');
    const dateParam = searchParams.get('date');
    const deepLinkKey = eventId || dateParam;
    if (!deepLinkKey || handledDeepLinkRef.current === deepLinkKey) return;

    if (eventId) {
      const event = events.find((candidate) => candidate.id === eventId);
      if (!event) return;
      setCurrentDate(event.start);
      setSelectedEvent(event);
      handledDeepLinkRef.current = deepLinkKey;
      return;
    }

    if (dateParam) {
      const parsed = new Date(dateParam);
      if (!Number.isNaN(parsed.getTime())) {
        setCurrentDate(parsed);
        handledDeepLinkRef.current = deepLinkKey;
      }
    }
  }, [events, searchParams, setCurrentDate]);

  // Handle event click - show detail modal
  const handleEventClick = (event: CalendarEvent) => {
    setSelectedEvent(event);
  };

  // Handle slot selection for new event creation
  const handleSlotSelect = (slotInfo: { start: Date; end: Date; slots: Date[] }) => {
    setSlotSelection({ start: slotInfo.start, end: slotInfo.end });
    setEventFormOpen(true);
  };

  // Handle new event button click
  const handleNewEventClick = () => {
    setSlotSelection(null);
    setEditingEvent(null);
    setEventFormOpen(true);
  };

  // Handle edit event from detail modal
  const handleEditEvent = () => {
    if (selectedEvent) {
      setEditingEvent(selectedEvent);
      setSelectedEvent(null);
      setEventFormOpen(true);
    }
  };

  // Handle close event form
  const handleCloseEventForm = () => {
    setEventFormOpen(false);
    setSlotSelection(null);
    setEditingEvent(null);
  };

  // Handle close event detail modal
  const handleCloseEventDetail = () => {
    setSelectedEvent(null);
  };

  // Navigation is now handled by FullCalendar's native controls

  return (
    <CalendarDataProvider>
      <div className="h-full min-h-0 flex bg-gray-50 dark:bg-gray-900 relative">
      {/* Real-time Notifications */}
      <RealTimeNotifications />

      {/* Floating Hamburger Icon - Only visible when sidebar is closed */}
      {!isCalendarSidebarOpen && (
        <div className="absolute top-4 left-4 z-50">
          <button
            onClick={() => setCalendarSidebarOpen(true)}
            className="flex items-center gap-2 bg-white dark:bg-gray-800 px-3 py-2 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            title="Open sidebar"
          >
            <Bars3Icon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Menu</span>
          </button>
        </div>
      )}

      {/* Main Content Area - Full Height */}
      <div className="flex-1 flex overflow-hidden h-full min-h-0">
        {/* Compact Calendar Sidebar */}
        <CalendarSidebar onCreateEvent={handleNewEventClick} />
        
        {/* Calendar Content - Full Viewport Maximized */}
        <div className="flex-1 relative bg-white dark:bg-gray-800 h-full min-h-0">
          <FullCalendarView
            onEventClick={handleEventClick}
            onSlotSelect={handleSlotSelect}
          />
        </div>
      </div>

      {/* Event Form Modal */}
      {isEventFormOpen && (
        <EventForm
          isOpen={isEventFormOpen}
          onClose={handleCloseEventForm}
          event={editingEvent || undefined}
          initialStart={slotSelection?.start}
          initialEnd={slotSelection?.end}
        />
      )}

      {/* Event Detail Modal */}
      {selectedEvent && (
        <EventDetailModal
          event={selectedEvent}
          isOpen={!!selectedEvent}
          onClose={handleCloseEventDetail}
          onEdit={handleEditEvent}
        />
      )}
      </div>
    </CalendarDataProvider>
  );
};

export default CalendarPage;
